import {
  SignalingMessageUserlist,
  SignalingService,
  UserProps
} from "../../../services";
import { BaseStreamingCommand } from "./BaseStreamingCommand";
import { Peer } from "@domain";
import {
  RoomStreamingBloc,
  UserTypeFilterConfig
} from "../../../RoomStreamingBloc";
import { CreateRemoteParticipantInteractor } from "../../../interactors";
import { PeerHandler } from "../../PeerHandler";
import { UniqueEntityId } from "@kernel/ddd";

export class RefreshUserlistCommand extends BaseStreamingCommand {
  constructor(
    bloc: RoomStreamingBloc,
    signalingService: SignalingService,
    private readonly config: RTCConfiguration
  ) {
    super(bloc, signalingService);
  }
  public execute(e: unknown): void {
    const event = e as CustomEvent<SignalingMessageUserlist>;
    const userTypeFilter = this.bloc.getUserTypeFilter();
    const remoteUsers = this.filterUserlistByUserTypeFilter(
      this.filterUserlistFromLocalParticipant(
        this.filterUserlistFromRemoteParticipants(event.detail.data.users)
      ),
      userTypeFilter
    );
    for (const user of remoteUsers) {
      if (!this.getRoom().hasRemoteParticipant(new UniqueEntityId(user.id))) {
        this.createRemoteParticipant(user);
      }
    }
  }

  private filterUserlistByUserTypeFilter(
    users: UserProps[],
    filter: UserTypeFilterConfig
  ): UserProps[] {
    if (filter === null) return users;

    return users.filter((user) => filter.types.includes(user.type));
  }

  private filterUserlistFromLocalParticipant(users: UserProps[]): UserProps[] {
    const room = this.getRoom();
    const localParticipant = room.localParticipant;
    if (localParticipant === null) return users;
    return users.filter((user) => {
      if (!localParticipant.remoteId) return false;
      return user.id !== localParticipant.remoteId.toValue();
    });
  }

  private filterUserlistFromRemoteParticipants(
    users: UserProps[]
  ): UserProps[] {
    const room = this.getRoom();
    const remoteParticipants = room.remoteParticipants.items;
    return users.filter((user) => {
      return !remoteParticipants.some((participant) => {
        if (!participant.remoteId) return false;
        return user.id === participant.remoteId.toValue();
      });
    });
  }

  private createRemoteParticipant(user: UserProps): void {
    const room = this.getRoom();
    const peer = this.createPeerConnection(user);
    const createRemoteParticipantInteractor =
      CreateRemoteParticipantInteractor.create();
    const remoteParticipant = createRemoteParticipantInteractor.execute({
      displayName: user.username,
      remoteId: user.id,
      type: user.type,
      peer,
      room
    });
    room.joinRemoteParticipant(remoteParticipant);
    this.bloc.changeState({
      ...this.bloc.state,
      room
    });
    this.bloc.handleNotification({
      header: "Connessione",
      body: `${user.username} si è connesso alla stanza`
    });
  }

  private createPeerConnection(user: UserProps): Peer {
    console.log("createPeerConnection per user", user);
    const peer = Peer.create({
      polite: this.isPoliteNode(user.id),
      config: this.config,
      user
    });
    this.setLocalParticipantTrack(peer);
    return peer;
  }

  private isPoliteNode(remoteParticipantRemoteId: number): boolean {
    const localParticipantRemoteId = this.getRoomLocalParticipantRemoteId();
    return localParticipantRemoteId < remoteParticipantRemoteId;
  }

  private setLocalParticipantTrack(peer: Peer): void {
    const room = this.getRoom();
    const localParticipant = room.localParticipant;
    if (localParticipant === null) throw new Error("Local participant is null");
    if (localParticipant.stream) {
      for (const track of localParticipant.stream.getTracks()) {
        peer.addTrack(track);
      }
    }
    const hasAudio = localParticipant.stream?.hasTrack("audio") ?? false;
    const hasVideo = localParticipant.stream?.hasTrack("video") ?? false;
    if (!hasAudio) {
      peer.addTransceiver("audio", { direction: "recvonly" });
    }
    if (!hasVideo) {
      peer.addTransceiver("video", { direction: "recvonly" });
    }
    PeerHandler.create(peer, this.bloc, this.signalingService);
  }
}
